/**
 * PrayerScheduleStrip — fixed 5-prayer strip (Fajr, Dhuhr, Asr, Maghrib, Isha).
 *
 * Format:
 *   [Fajr ✓] | [Dhuhr ✓] | [Asr — NEXT] | [Maghrib] | [Isha]
 *
 * - Past prayer: faded + ✓ if the habit was logged for today.
 * - Current prayer (most recently passed): accent border.
 * - Next prayer: accent background + "NEXT" badge.
 * - Each chip is an individual <a href="#/prayer"> link.
 * - Re-renders every 60s via setInterval; pauses when tab hidden.
 * - Reduced-motion: transitions disabled when prefers-reduced-motion.
 * - RTL-aware (flex-direction inherited from [dir="rtl"] parent).
 *
 * Done-state: reads aweni_yawmi_habit_logs localStorage key.
 * Habit IDs: p_fajr | p_dhuhr | p_asr | p_maghrib | p_isha.
 */

import type { AweniContext } from '../../types';
import { prayerTimeService } from '../../services/PrayerTimeService';
import { getLocationService } from '../../services/LocationService';

/** localStorage keys shared with HabitTab — do NOT change without migrating HabitTab too. */
const HABIT_LOGS_KEY = 'aweni_yawmi_habit_logs';
const HABITS_KEY = 'aweni_yawmi_habits';

/** Map of the 5 obligatory prayers to their canonical habit IDs. */
const PRAYER_HABIT_ID: Record<string, string> = {
  fajr: 'p_fajr',
  dhuhr: 'p_dhuhr',
  asr: 'p_asr',
  maghrib: 'p_maghrib',
  isha: 'p_isha',
};

/** Read today's done-set from localStorage (graceful if missing/corrupt). */
function readTodayDoneSet(): Set<string> {
  const today = new Date().toISOString().slice(0, 10);
  const done = new Set<string>();
  try {
    const logs: Array<{ habitId: string; date: string; count: number }> =
      JSON.parse(localStorage.getItem(HABIT_LOGS_KEY) || '[]');
    for (const l of logs) {
      if (l.date === today && l.count > 0) done.add(l.habitId);
    }
  } catch { /* ignore */ }
  return done;
}

/**
 * Ensure the 5 prayer habits exist in HABITS_KEY so users can log them from
 * HabitTab as well. Only adds missing ones — never overwrites existing data.
 */
function ensurePrayerHabits(): void {
  try {
    const raw = localStorage.getItem(HABITS_KEY);
    const habits: Array<{ id: string; [k: string]: unknown }> =
      raw ? JSON.parse(raw) : [];
    const existing = new Set(habits.map(h => h.id));
    const prayerNames: Record<string, string> = {
      p_fajr: 'yawmi_prayer_fajr',
      p_dhuhr: 'yawmi_prayer_dhuhr',
      p_asr: 'yawmi_prayer_asr',
      p_maghrib: 'yawmi_prayer_maghrib',
      p_isha: 'yawmi_prayer_isha',
    };
    let changed = false;
    for (const [id, name] of Object.entries(prayerNames)) {
      if (!existing.has(id)) {
        habits.push({
          id, name, icon: '🕌', category: 'worship', frequency: 'daily',
          targetCount: 1, unit: '', streakCurrent: 0, streakBest: 0,
          createdAt: new Date().toISOString(),
        });
        changed = true;
      }
    }
    if (changed) localStorage.setItem(HABITS_KEY, JSON.stringify(habits));
  } catch { /* ignore — no side effects on failure */ }
}

export class PrayerScheduleStrip {
  private root: HTMLElement | null = null;
  private deps: { ctx: AweniContext };
  private locUnsub: (() => void) | null = null;
  private tick: ReturnType<typeof setInterval> | null = null;
  private lastTickAt = 0;

  constructor(deps: { ctx: AweniContext }) {
    this.deps = deps;
  }

  mount(parent: HTMLElement): void {
    if (this.root) return;
    this.injectStyles();

    const root = document.createElement('section');
    root.className = 'aweni-prayer-strip';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label',
      this.deps.ctx.i18n.t('prayer_banner_label') || 'Prayer schedule');
    parent.appendChild(root);
    this.root = root;
    this.render();

    this.locUnsub = getLocationService().onChange(() => this.render());
    this.tick = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      // On stale wakeup (tab was hidden >5min) recompute fully
      if (Date.now() - this.lastTickAt > 6 * 60_000) this.render();
      else this.renderCountdown();
      this.lastTickAt = Date.now();
    }, 60_000);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  private onVisibility = () => {
    if (document.visibilityState === 'visible') this.render();
  };

  refresh(): void { this.render(); }

  unmount(): void {
    this.locUnsub?.();
    this.locUnsub = null;
    if (this.tick !== null) { clearInterval(this.tick); this.tick = null; }
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.root?.remove();
    this.root = null;
  }

  /** Full re-render — replaces DOM chips. */
  private render(): void {
    if (!this.root) return;
    this.lastTickAt = Date.now();
    const locale = this.deps.ctx.i18n.locale;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);

    const loc = getLocationService().getState();
    if (loc.lat === null || loc.lng === null) {
      this.root.style.display = 'none';
      return;
    }

    const now = new Date();
    const strip = prayerTimeService.getTodayFivePrayerStrip(now);
    if (!strip || !strip.length) {
      this.root.style.display = 'none';
      return;
    }
    this.root.style.display = '';

    // Ensure prayer habits exist in localStorage so users can log them.
    ensurePrayerHabits();
    const doneSet = readTodayDoneSet();

    const fmt = (d: Date) => {
      try {
        return new Intl.DateTimeFormat(locale, {
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(d);
      } catch { return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); }
    };

    const countdown = (entry: { time: Date }): string => {
      const mins = Math.max(0, Math.round((entry.time.getTime() - now.getTime()) / 60_000));
      if (mins < 1) return t('prayer_in_moment') || 'now';
      if (mins < 60) return (t('prayer_in_min') || 'in {m}min').replace('{m}', String(mins));
      const h = Math.floor(mins / 60), m = mins % 60;
      return m === 0
        ? (t('prayer_in_h') || 'in {h}h').replace('{h}', String(h))
        : (t('prayer_in_hm') || 'in {h}h {m}min').replace('{h}', String(h)).replace('{m}', String(m));
    };

    // Find the "current" prayer = most recently passed obligatory prayer.
    // If e.g. Dhuhr just passed and Asr is next, Dhuhr is "current".
    const pastPrayers = strip.filter(e => e.isPast && !e.isNext);
    const currentEntry = pastPrayers.length ? pastPrayers[pastPrayers.length - 1] : null;

    const chips: string[] = [];
    for (const entry of strip) {
      const habitId = PRAYER_HABIT_ID[entry.name];
      const isDone = habitId ? doneSet.has(habitId) : false;
      const name = prayerTimeService.getPrayerName(entry.name, locale as 'en');
      const timeStr = fmt(entry.time);
      const isCurrent = currentEntry?.name === entry.name;

      let cls = 'aweni-pb-chip';
      if (entry.isPast && !isCurrent) cls += ' is-past';
      if (isCurrent) cls += ' is-current';
      if (entry.isNext) cls += ' is-next';

      const doneMarker = isDone
        ? `<span class="aweni-pb-done" aria-label="${escAttr(t('prayer_done_marker') || 'done')}">✓</span>`
        : '';

      const countdownBadge = entry.isNext
        ? `<span class="aweni-pb-countdown" role="timer" aria-live="off">${escHtml(countdown(entry))}</span>`
        : '';

      let ariaLabel = `${name} ${timeStr}`;
      if (isDone) ariaLabel += `, ${t('prayer_done_marker') || 'done'}`;
      if (entry.isNext) ariaLabel += `, ${countdown(entry)}`;

      chips.push(`
        <a class="${escAttr(cls)}" href="#/prayer" role="listitem"
           aria-current="${entry.isNext ? 'true' : 'false'}"
           aria-label="${escAttr(ariaLabel)}">
          <span class="aweni-pb-name">${escHtml(name)}</span>
          <span class="aweni-pb-time">${escHtml(timeStr)}</span>
          ${doneMarker}${countdownBadge}
        </a>`);
    }

    this.root.innerHTML = `
      <div class="aweni-pb-row" role="list" aria-live="polite" aria-atomic="false">
        ${chips.join('')}
      </div>`;
  }

  /** Light tick — only mutates countdown text without full DOM replacement. */
  private renderCountdown(): void {
    if (!this.root) return;
    const now = new Date();
    const strip = prayerTimeService.getTodayFivePrayerStrip(now);
    if (!strip) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const nextEntry = strip.find(e => e.isNext);
    if (!nextEntry) return;
    const mins = Math.max(0, Math.round((nextEntry.time.getTime() - now.getTime()) / 60_000));
    const text = mins < 1
      ? (t('prayer_in_moment') || 'now')
      : mins < 60
        ? (t('prayer_in_min') || 'in {m}min').replace('{m}', String(mins))
        : (() => {
            const h = Math.floor(mins / 60), m = mins % 60;
            return m === 0
              ? (t('prayer_in_h') || 'in {h}h').replace('{h}', String(h))
              : (t('prayer_in_hm') || 'in {h}h {m}min').replace('{h}', String(h)).replace('{m}', String(m));
          })();
    const el = this.root.querySelector<HTMLElement>('.aweni-pb-countdown');
    if (el) el.textContent = text;
  }

  private injectStyles(): void {
    if (document.getElementById('aweni-pb-styles')) return;
    const style = document.createElement('style');
    style.id = 'aweni-pb-styles';
    style.textContent = `
      .aweni-prayer-strip { margin: 0 0 16px; }
      .aweni-pb-row {
        display: flex;
        gap: 0;
        align-items: stretch;
        background: var(--card-bg, #fff);
        border: 1px solid var(--border, #E5E7EB);
        border-radius: 14px;
        overflow: hidden;
      }
      .dark .aweni-pb-row {
        background: rgba(255,255,255,0.04);
        border-color: rgba(255,255,255,0.10);
      }
      .aweni-pb-chip {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        padding: 10px 4px;
        flex: 1 1 0;
        min-width: 0;
        text-align: center;
        text-decoration: none;
        color: inherit;
        border-right: 1px solid var(--border, #E5E7EB);
        transition: background var(--motion-duration-fast, 150ms) var(--motion-easing-default, ease);
      }
      .dark .aweni-pb-chip { border-right-color: rgba(255,255,255,0.10); }
      .aweni-pb-chip:last-child { border-right: none; }
      .aweni-pb-chip:hover { background: rgba(0,0,0,0.03); }
      .dark .aweni-pb-chip:hover { background: rgba(255,255,255,0.05); }
      .aweni-pb-chip:focus-visible {
        outline: 2px solid var(--accent, #0A0A0A);
        outline-offset: -2px;
        border-radius: 4px;
      }
      .aweni-pb-chip.is-past { opacity: 0.45; }
      .aweni-pb-chip.is-current {
        background: rgba(10,10,10,0.04);
        opacity: 0.75;
      }
      .dark .aweni-pb-chip.is-current { background: rgba(255,255,255,0.06); }
      .aweni-pb-chip.is-next {
        background: rgba(10,10,10,0.06);
        border-right-color: rgba(10,10,10,0.12);
      }
      .dark .aweni-pb-chip.is-next {
        background: rgba(255,255,255,0.08);
        border-right-color: rgba(255,255,255,0.15);
      }
      .aweni-pb-name {
        font-size: 9px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: var(--text-secondary, #6B7280);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
      .aweni-pb-chip.is-next .aweni-pb-name,
      .aweni-pb-chip.is-current .aweni-pb-name { color: var(--accent, #0A0A0A); }
      .dark .aweni-pb-chip.is-next .aweni-pb-name,
      .dark .aweni-pb-chip.is-current .aweni-pb-name { color: var(--ln-accent, #9C8348); }
      .aweni-pb-time {
        font-size: 13px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--text-primary, #1E293B);
        white-space: nowrap;
      }
      .dark .aweni-pb-time { color: #F1F5F9; }
      .aweni-pb-chip.is-next .aweni-pb-time { color: var(--accent, #0A0A0A); }
      .dark .aweni-pb-chip.is-next .aweni-pb-time { color: var(--ln-accent, #9C8348); }
      .aweni-pb-done {
        font-size: 11px;
        font-weight: 700;
        color: #22c55e;
        line-height: 1;
      }
      .aweni-pb-countdown {
        font-size: 9px;
        font-weight: 500;
        color: var(--accent, #0A0A0A);
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
      .dark .aweni-pb-countdown { color: var(--ln-accent, #9C8348); }
      @media (max-width: 380px) {
        .aweni-pb-time { font-size: 12px; }
        .aweni-pb-name { font-size: 8px; }
      }
      @media (prefers-reduced-motion: reduce) {
        .aweni-pb-chip { transition: none; }
      }
    `;
    document.head.appendChild(style);
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
